<template>
  <div class="shipping-holder">

    <h1>{{ t.shipping_and_payment }}</h1>

    <form method="post" id="webshop-shipping" v-on:submit.prevent>

      <div class="row">

        <div class="col-md-6">

          <fieldset>

            <legend>{{ t.shipping }}</legend>

            <div class="sap-item" v-for="shipping in orderData.shippingArray" :key="'shipping_'+shipping.id">
              <label class="radio-container">
                <input type="radio" name="shipping_type"
                       v-on:change="changeShipping"
                       v-model="formData.shippingId"
                       :value="shipping.id"
                       :checked="formData.shippingId">

                <span class="checkmark"></span> {{ shipping.name }}
              </label>

              <div class="sap-description" v-if="formData.shippingId == shipping.id" v-html="shipping.description"></div>

            </div>

            <p class="input-error-hint" v-if="error.shipping_error" v-html="error.shipping_error"></p>
            <div class="sap-item">
              <label  class="radio-container" >
                <input type="checkbox"
                       v-model="formData.acceptShippingCondition"
                       :checked="formData.acceptShippingCondition">&nbsp;
                <span v-html="t.accept_shipping_conditions"></span>
              </label>

            <p class="input-error-hint" v-if="error.accept_shipping_condition" v-html="error.accept_shipping_condition"></p>
            </div>
          </fieldset>

          <fieldset>

            <legend>{{ t.payment }}</legend>

            <div v-if="!formData.shippingId">{{ t.please_select_shipping }}</div>

            <div class="sap-item" v-for="(payment, index) in orderData.paymentArray" :key="index" v-show="payment.shipping_id === parseInt(formData.shippingId) && isShowablePayment(payment)">
              <label class="radio-container">
                <input type="radio" :name="'payment_type_' + payment.shipping_id"
                       v-on:change="calcShipping"
                       v-model="formData.paymentId"
                       :value="payment.id"
                       :checked="formData.paymentId">

                <span class="checkmark"></span> {{ payment.name }}
              </label>
              <div class="sap-description" v-if="formData.paymentId == payment.id" v-html="payment.description"></div>
            </div>

            <p class="input-error-hint" v-if="error.payment_error" v-html="error.payment_error"></p>

          </fieldset>

          <fieldset v-if="priceTableArray.length">
            <legend>{{ t.shipping_price }}</legend>

            <table class="table">
              <thead>
                <tr>
                  <th v-if="settingData.isWeightBasedShipping === '1'">{{ t.from_weight }}</th>
                  <th v-if="settingData.isWeightBasedShipping === '1'">{{ t.to_weight }}</th>
                  <th v-if="settingData.isFrontendPriceNet === '1'">{{ t.net_price }}</th>
                  <th v-if="settingData.isFrontendPriceNet === '0'">{{ t.gross_price }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(priceItem, priceIndex) in priceTableArray" :key="priceIndex">
                  <td v-if="settingData.isWeightBasedShipping === '1'">{{ priceItem.from_weight }} kg</td>
                  <td v-if="settingData.isWeightBasedShipping === '1'">{{ priceItem.to_weight }} kg</td>
                  <td v-if="settingData.isFrontendPriceNet === '0'">{{ formatNumber(roundNumber(priceItem.price)) }}</td>
                  <td v-if="settingData.isFrontendPriceNet === '1'">{{ formatNumber(roundNumber(priceItem.net_price)) }}</td>
                </tr>
              </tbody>
            </table>

          </fieldset>

          <div class="webshop-shipping-price" v-if="formData.shippingId">

            <p v-if="settingData.isWeightBasedShipping" class="products-weight">
              <strong>{{ t.products_weight }}</strong>: {{ productData.products_weight }} kg
            </p>

            <p v-if="showSAP" class="products-shipping-payment">
              <strong>{{ shippingName }}, {{ paymentName }}</strong>: {{ shippingPrice ? formatNumber(shippingPrice) + ' + ' + t.vat : t.free }}
            </p>

            <p v-if="formData.shippingAbroad" class="shipping-message">
              <strong>{{ t.shipping_message }}</strong>
            </p>

            <p v-if="showSAP" class="products-order-price">
              <strong>{{ t.total_amount }}</strong>: {{ formatNumber(orderPrice) }} + {{ t.vat }}
            </p>

          </div>

        </div>

        <div class="col-md-6">

          <fieldset v-if="showShippingForm">

            <legend>{{ t.shipping_detail }}</legend>

            <button class="btn btn-primary-small" style="margin-bottom: 15px;" type="button" v-on:click="copyInvoice">{{ t.copy_invoice_address }}</button>

            <div class="form-group" v-if="formData.shippingArray.length">
              <label class="control-label" for="shipping_id"><strong>{{ t.shipping_addresses }}</strong></label>
              <select id="shipping_id" class="form-control" name="shipping_id"
                      v-on:change="shippingChange"
                      v-model="formData.shippingAddress.shipping_id">
                <option value="0">{{ t.create_new_address }}</option>
                <option v-for="address in formData.shippingArray" :key="address.id" :value="address.id">{{ address.name }}</option>
              </select>
            </div>

            <div class="form-group" v-if="formData.countryArray.length">
              <label class="control-label" for="shipping_country">{{ t.shipping_country }}</label>
              <select id="shipping_country" class="form-control" name="shipping_country"
                      v-on:change="countryChange"
                      v-model="formData.shippingAddress.shipping_country"
                      v-bind:class="{ 'is-invalid' : error.shipping_country }"
                      @focus="error.shipping_country = null">

                <option v-for="country in formData.countryArray" :key="country.id" :value="country.id">{{ country.name }}</option>
              </select>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_name">{{ t.shipping_name }}*</label>
              <input type="text" id="shipping_name" class="form-control" name="shipping_name"
                     v-bind:placeholder="t.shipping_name"
                     v-model="formData.shippingAddress.shipping_name"
                     v-bind:class="{ 'is-invalid' : error.shipping_name }"
                     @focus="error.shipping_name = null">

              <p class="input-error-hint" v-if="error.shipping_name" v-html="error.shipping_name"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_postal">{{ t.shipping_postal }}*</label>
              <input type="text" id="shipping_postal" class="form-control" name="shipping_postal"
                    v-on:change="postalChange"
                     v-on:keyup="calcShipping"
                     v-bind:placeholder="t.shipping_postal"
                     v-model="formData.shippingAddress.shipping_postal"
                     v-bind:class="{ 'is-invalid' : error.shipping_postal }"
                     @focus="error.shipping_postal = null">

              <p class="input-error-hint" v-if="error.shipping_postal" v-html="error.shipping_postal"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_city">{{ t.shipping_city }}*</label>
              <input type="text" id="shipping_city" class="form-control" name="shipping_city"
                     v-bind:placeholder="t.shipping_city"
                     v-model="formData.shippingAddress.shipping_city"
                     v-bind:class="{ 'is-invalid' : error.shipping_city }"
                     @focus="error.shipping_city = null">

              <p class="input-error-hint" v-if="error.shipping_city" v-html="error.shipping_city"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_address">{{ t.shipping_address }}*</label>
              <input type="text" id="shipping_address" class="form-control" name="shipping_address"
                     v-bind:placeholder="t.shipping_address"
                     v-model="formData.shippingAddress.shipping_address"
                     v-bind:class="{ 'is-invalid' : error.shipping_address }"
                     @focus="error.shipping_address = null">

              <p class="input-error-hint" v-if="error.shipping_address" v-html="error.shipping_address"></p>
            </div>

            <div class="form-group">
              <label class="control-label" for="shipping_door">{{ t.shipping_door }}</label>
              <input type="text" id="shipping_door" class="form-control" name="shipping_door"
                     v-bind:placeholder="t.shipping_door"
                     v-model="formData.shippingAddress.shipping_door"
                     v-bind:class="{ 'is-invalid' : error.shipping_door }"
                     @focus="error.shipping_door = null">
            </div>

          </fieldset>

        </div>

      </div>

    </form>

    <div class="webshop-step-holder">

      <div class="webshop-step-left">
        <div class="step-button">
          <router-link :to="{ name: 'Customer' }" class="btn btn-black-small text-red">
            <i class="fa fa-arrow-left" aria-hidden="true"></i> {{ t.back }}
          </router-link>
        </div>
      </div>

      <div class="webshop-step-right">
        <div class="step-button">
          <button class="btn btn-primary-small" v-on:click="saveShippingForm">{{ t.next }} <i class="fa fa-arrow-right" aria-hidden="true"></i></button>
        </div>
      </div>

    </div>

  </div>
</template>


<script>
import {mapGetters} from "vuex";
import axios from 'axios'

export default {
  name: 'Shipping',
  data() {
    return {
      // Szállítás adatlap
      showShippingForm: false,
      // Ártáblázat (Súly, Ország)
      priceTableArray: [],
      // Szállítási és Fizetés
      shippingPrice: 0,
      shippingName: '',
      paymentName: '',
      showShippingAndPayment: false,
      showSAP: false,
      // Kosár értéke
      orderPrice: 0,
    }
  },
  computed: {
    ...mapGetters({
      t: "getLanguageData",
      error: "getErrorData",
      userData: "getUserData",
      orderData: "getOrderData",
      productData: "getProductData",
      settingData: "getSettingData",
      formData: "getFormData",
      currentStep: "getCurrentStep",
      orderSummaryData: "getOrderSummaryData",
    }),
  },
  methods: {
    isShowablePayment: function (payment){
      let products = this.productData.products;
      let isCut = false;
      Object.keys(products).forEach(key => {
        if(products[key].size != '-'){
          isCut = true;
        }
      });
      if(!isCut || (isCut && payment.enable_unique_size)){
        return true;
      }
      return false;
    },
    saveShippingForm: function (){
      let $this = this;

      let formData = {
        'page'                : 'Shipping',
        'method'              : 'Save',
        'formData'            : $this.formData
      };

      $this.$store.dispatch('setData', formData);
    },
    getShippingPrice: function (){
      let $this = this;

      let formData = {
        'page'                : 'Shipping',
        'method'              : 'GetPrice',
        'formData'            : $this.formData
      };

      $this.$store.dispatch('setData', formData);
    },
    changeShipping: function (){
      // Payment Remove Checked
      this.formData.paymentId = null;
      this.calcShipping();
    },
    calcShipping: function (){
      let _this             = this;
      let $shippingType     = this.getArrayItem(this.orderData.shippingArray, this.formData.shippingId);
      let $paymentType      = this.getArrayItem(this.orderData.paymentArray, this.formData.paymentId);
      let $shippingPostal   = this.formData.shippingAddress.shipping_postal;

      // Reset
      _this.shippingName    = '';
      _this.paymentName     = '';
      _this.shippingPrice   = 0;
      _this.priceTableArray = [];
      _this.showShippingForm= false;
      _this.showShippingAndPayment = false;

      _this.error.shipping_error  = '';
      _this.error.payment_error   = '';

      if(!$shippingType){
        return false;
      }

      // Shipping
      _this.shippingName = $shippingType.name;

      // Shipping Form
      if(parseInt($shippingType.is_shipping_required)){
        this.showShippingForm = true;
      }

      // Payment
      if(!$paymentType){
        return false;
      }

      _this.paymentName = $paymentType.name;

      // Ha irányítószám nagyobb mint 4 és van ShippingForm
      if(this.showShippingForm){
        // Minimum 4 karaktert el kell érnie!
        if($shippingPostal.length >= 4){
          _this.getShippingPrice();
        }else{
          _this.showShippingAndPayment = false;
        }
        return true;
      }

      // showSAP átírva, különfüggvénybe ne szervezzük ki, mert ezek computed értékek és nem is értem hogy futott le a fordítás hiba nélkül.
      // Logikát nem tudom, de ha kell írjuk át én csak javítottam, mert elszállt fordítás nálam.
      let shippingCountry = this.formData.shippingAddress.shipping_country;
      this.formData.shippingAbroad = (shippingCountry == 1 || shippingCountry == 4 ? false : true);
      this.showSAP = !this.formData.shippingAbroad && _this.showShippingAndPayment;

      _this.getShippingPrice();
    },
    shippingChange: function(){
      let $addressId = this.formData.shippingAddress.shipping_id;

      if(!$addressId){
        return true;
      }

      let $selectedAddress = this.getArrayItem(this.formData.shippingArray, $addressId);

      if($selectedAddress !== null){
        if($selectedAddress.shipping_country)
          this.formData.shippingAddress.shipping_country  = $selectedAddress.shipping_country;

        this.formData.shippingAddress.shipping_name     = $selectedAddress.shipping_name;
        this.formData.shippingAddress.shipping_postal   = $selectedAddress.shipping_postal;
        this.formData.shippingAddress.shipping_city     = $selectedAddress.shipping_city;
        this.formData.shippingAddress.shipping_address  = $selectedAddress.shipping_address;
        this.formData.shippingAddress.shipping_door     = $selectedAddress.shipping_door;
      }
      this.$store.commit("setFormData",  this.formData);

      this.calcShipping();
    },
    copyInvoice: function (){
      let $invoiceAddress = this.formData.invoiceAddress;
      this.formData.shippingAddress.shipping_country  = $invoiceAddress.invoice_country;
      this.formData.shippingAddress.shipping_name     = $invoiceAddress.invoice_name;
      this.formData.shippingAddress.shipping_postal   = $invoiceAddress.invoice_postal;
      this.formData.shippingAddress.shipping_city     = $invoiceAddress.invoice_city;
      this.formData.shippingAddress.shipping_address  = $invoiceAddress.invoice_address;
      this.formData.shippingAddress.shipping_door     = $invoiceAddress.invoice_door;
      this.$store.commit("setFormData",  this.formData);
      this.calcShipping();
    },
    countryChange: function(){
            this.formData.shippingAddress.shipping_name     = '';
            this.formData.shippingAddress.shipping_postal   = '';
            this.formData.shippingAddress.shipping_city     = '';
            this.formData.shippingAddress.shipping_address  = '';
            this.formData.shippingAddress.shipping_door     = '';
            this.formData.shippingAddress.shipping_tax_id   = '';

            this.calcShipping();
    },
    postalChange: function(){
        let $country = this.formData.shippingAddress.shipping_country;
        let $postal = this.formData.shippingAddress.shipping_postal;

        if(!$postal){
            return true;
        }

       let data = {
           country: $country,
           postal: $postal
       };

       let params = this.encodeQueryData(data);

       axios.get(this.settingData.postalUrl + '?' + params)
           .then((res) => {
                this.formData.shippingAddress.shipping_city = res.data.city;
           })
           .catch((err) => {
               // eslint-disable-next-line
               console.error(err);
           });
    },

    // Kisegítő függvények
    getArrayItem: function ($addressArray, $addressId){
      for(let i in $addressArray){
        let item = $addressArray[i];
        if(parseInt(item.id) === parseInt($addressId)){
          return item;
        }
      }
      return null;
    },
    encodeQueryData(data) {
        const ret = [];
        for (let d in data)
            ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(data[d]));
        return ret.join('&');
    },
    formatNumber: function (number){
      let formatter = new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: this.orderData.currencyCode,
        minimumFractionDigits: 0
      });

      return formatter.format(number);
    },
    roundNumber: function (number){
      let _this = this;
      let $productPriceNetOrGross = _this.settingData.productPriceNetOrGross;

      let $ten = _this.orderData.currencyTen;
      let $round = _this.orderData.currencyRound;
      let $precision = _this.orderData.currencyNetPrecision;

      if($productPriceNetOrGross === '1'){
        $precision = _this.orderData.currencyGrossPrecision;
      }

      // Round
      if($round === 0){
        return parseFloat((Math.round(number * $ten) / $ten).toFixed($precision));
      }

      // Ceil
      if($round === 1){
        return parseFloat((Math.ceil(number * $ten) / $ten).toFixed($precision));
      }

      // Floor
      if($round === 2){
        return parseFloat((Math.floor(number * $ten) / $ten).toFixed($precision));
      }
    }
  },
  created() {
    /** Betöltés utáni számolás / megjelenítés */
    this.calcShipping();

    this.$store.commit("setCurrentStep", 'Shipping');
  },
  updated() {
    // Ha "saveShipping" lefutott és minden rendben volt, akkor Step is változott, tehát haladunk előre...
    if(this.currentStep === 'Summary'){
      this.$router.push({ name: 'Summary' });
    }

    // Szállítási árak frissítése
    if(this.currentStep === 'updateShippingPrice'){
      this.showShippingAndPayment = true;
      this.orderPrice             = this.orderSummaryData.order_price;
      this.shippingPrice          = this.orderSummaryData.shipping_price;
      this.$store.commit("setCurrentStep", 'Shipping');
    }
  }
}
</script>